import { useState, useMemo, useEffect, useRef } from "react";
import { Drawer, Button, Select, DatePicker, notification } from "antd";
import dayjs, { Dayjs } from "dayjs";
import type { FilterValues } from "./types";
import {
  useToteStatus,
  useTrolleyStatus,
  usePrimaryLocations,
  useTotePriorities,
} from "../../pages/common/queries";
import "./FilterModal.scss";

const normalizeStringArray = (
  values: Array<string | null | undefined>
): string[] =>
  values
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter((value) => value.length > 0);

const derivePriorityState = (
  source: FilterValues | null | undefined
): string => {
  const raw = source?.priority;

  if (!raw) {
    return "";
  }

  if (Array.isArray(raw)) {
    const normalized = normalizeStringArray(raw);
    return normalized[0] ?? "";
  }

  const trimmed = raw.trim();
  return trimmed;
};

const deriveStatusState = (
  source: FilterValues | null | undefined
): string[] => {
  const raw = source?.status;

  if (!raw) {
    return [];
  }

  if (Array.isArray(raw)) {
    return normalizeStringArray(raw);
  }

  const trimmed = raw.trim();
  return trimmed ? [trimmed] : [];
};

const toDayjsOrNull = (value?: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const extractStateFromFilters = (source: FilterValues | null | undefined) => {
  const priorityState = derivePriorityState(source);
  const statusState = deriveStatusState(source);
  const startDateState = source?.from_date
    ? toDayjsOrNull(source.from_date)
    : Array.isArray(source?.dateRange) && source?.dateRange.length > 0
    ? toDayjsOrNull(source?.dateRange[0])
    : null;
  const endDateState = source?.to_date
    ? toDayjsOrNull(source.to_date)
    : Array.isArray(source?.dateRange) && source?.dateRange.length > 1
    ? toDayjsOrNull(source?.dateRange[1])
    : null;
  const locationIdState =
    typeof source?.location_id === "number" ? source.location_id : undefined;

  return {
    priorityState,
    statusState,
    startDateState,
    endDateState,
    locationIdState,
  };
};

interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterValues) => void;
  filterType: "tote" | "trolley";
  initialValues?: FilterValues | null;
  defaultValues?: FilterValues;
}

const generateRange = (start: number, end: number): number[] => {
  if (end <= start) {
    return [];
  }
  return Array.from({ length: end - start }, (_, index) => start + index);
};

const FilterModal = ({
  open,
  onClose,
  onApply,
  filterType,
  initialValues,
  defaultValues,
}: FilterModalProps) => {
  const isTote = filterType === "tote";
  const [selectedLocationId, setSelectedLocationId] = useState<
    number | undefined
  >(undefined);
  const [startDateTime, setStartDateTime] = useState<Dayjs | null>(null);
  const [endDateTime, setEndDateTime] = useState<Dayjs | null>(null);
  const [priority, setPriority] = useState<string>("");
  const [status, setStatus] = useState<string[]>([]);
  // Track if this is the first time the modal is opened to determine if we should use defaults
  const isFirstOpen = useRef<boolean>(true);

  // Fetch status options from API
  const { data: toteStatusData } = useToteStatus();
  const { data: trolleyStatusData } = useTrolleyStatus();
  const { data: primaryLocations } = usePrimaryLocations();

  const { data: totePriorityData } = useTotePriorities();

  const priorityOptions = useMemo(() => {
    if (isTote) {
      return totePriorityData?.map((priority) => priority.priority) || [];
    }
    return ["Standard", "High"];
  }, [isTote, totePriorityData]);

  // Use API data for status options, fallback to hardcoded if API fails
  // Memoize to prevent infinite re-renders by using stable string representations
  const toteStatusKey = useMemo(
    () =>
      toteStatusData?.tote_list
        ? toteStatusData.tote_list
            .map((s) => `${s.id}:${s.status_name}`)
            .join("|")
        : "",
    [toteStatusData?.tote_list]
  );
  const trolleyStatusKey = useMemo(
    () =>
      trolleyStatusData?.trolley_list
        ? trolleyStatusData.trolley_list
            .map((s) => `${s.id}:${s.status_name}`)
            .join("|")
        : "",
    [trolleyStatusData?.trolley_list]
  );

  const statusOptions = useMemo(() => {
    if (isTote) {
      return (
        toteStatusData?.tote_list?.map((status) => status.status_name) || [
          "Available",
          "Loaded to Trolley",
          "Unloaded from Trolley",
        ]
      );
    } else {
      return (
        trolleyStatusData?.trolley_list?.map(
          (status) => status.status_name
        ) || [
          "Empty - Available",
          "Loading",
          "Closed",
          "Unloading",
          "Empty - Not Available",
          "Unserviceable - Awaiting Repair",
        ]
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTote, toteStatusKey, trolleyStatusKey]);

  const locationOptions = useMemo((): Array<{
    label: string;
    value: number;
  }> => {
    if (!primaryLocations?.length) {
      return [];
    }

    return primaryLocations.map((location) => ({
      label: location.location_name,
      value: location.id,
    }));
  }, [primaryLocations]);

  const locationMap = useMemo(() => {
    const map = new Map<number, { label: string }>();
    if (!primaryLocations?.length) {
      return map;
    }
    primaryLocations.forEach((location) => {
      map.set(location.id, { label: location.location_name });
    });
    return map;
  }, [primaryLocations]);

  useEffect(() => {
    if (!open) {
      // Reset first open flag when modal closes
      isFirstOpen.current = true;
      return;
    }

    // Determine which values to use:
    // 1. If initialValues is explicitly provided (even if empty object), use it
    // 2. If initialValues is undefined AND this is the first open, use defaultValues
    // 3. Otherwise, use empty state (filters were explicitly cleared)
    let valuesToUse: FilterValues | null | undefined;
    if (initialValues !== undefined) {
      // Explicitly provided (could be empty object {} when filters were cleared)
      valuesToUse = initialValues;
    } else if (isFirstOpen.current && defaultValues) {
      // First time opening modal, use defaults
      valuesToUse = defaultValues;
    } else {
      // Not first open and no initialValues, use empty state
      valuesToUse = null;
    }

    const {
      priorityState,
      statusState,
      startDateState,
      endDateState,
      locationIdState,
    } = extractStateFromFilters(valuesToUse);

    setPriority(priorityState);
    setStatus(statusState);
    setStartDateTime(startDateState);
    setEndDateTime(endDateState);
    setSelectedLocationId(locationIdState);

    // Mark that we've opened the modal at least once
    isFirstOpen.current = false;
  }, [open, initialValues, defaultValues, isTote, filterType]);

  const showInvalidRangeWarning = () => {
    notification.warning({
      message: "Invalid Date Range",
      description:
        "End date & time cannot be earlier than the start date & time.",
    });
  };

  const handleStartDateChange = (value: Dayjs | null) => {
    setStartDateTime(value);
    if (value && endDateTime && value.isAfter(endDateTime)) {
      setEndDateTime(null);
    }
  };

  const handleEndDateChange = (value: Dayjs | null) => {
    if (value && startDateTime && value.isBefore(startDateTime)) {
      showInvalidRangeWarning();
      return;
    }
    setEndDateTime(value);
  };

  const disabledEndTime = (current: Dayjs | null) => {
    if (!startDateTime || !current) {
      return {
        disabledHours: () => [],
        disabledMinutes: () => [],
        disabledSeconds: () => [],
      };
    }

    if (!current.isSame(startDateTime, "day")) {
      return {
        disabledHours: () => [],
        disabledMinutes: () => [],
        disabledSeconds: () => [],
      };
    }

    const startHour = startDateTime.hour();
    const startMinute = startDateTime.minute();

    return {
      disabledHours: () => generateRange(0, startHour),
      disabledMinutes: (selectedHour: number) =>
        selectedHour === startHour ? generateRange(0, startMinute) : [],
      disabledSeconds: () => [],
    };
  };

  const buildFilterPayload = (
    priorityValue: string,
    statusValue: string[],
    selectedLocationIdValue: number | undefined,
    startDate: Dayjs | null,
    endDate: Dayjs | null
  ): FilterValues => {
    const fromDateStr = startDate
      ? startDate.format("YYYY-MM-DDTHH:mm:ss")
      : undefined;
    const toDateStr = endDate
      ? endDate.format("YYYY-MM-DDTHH:mm:ss")
      : undefined;

    const normalizedStatus = statusValue;

    let statusIds: number[] | undefined;
    if (normalizedStatus.length > 0) {
      if (isTote && toteStatusData?.tote_list) {
        statusIds = normalizedStatus
          .map(
            (statusName) =>
              toteStatusData.tote_list.find(
                (statusItem) => statusItem.status_name === statusName
              )?.id
          )
          .filter((id): id is number => typeof id === "number");
      } else if (!isTote && trolleyStatusData?.trolley_list) {
        statusIds = normalizedStatus
          .map(
            (statusName) => {
              // Try exact match first
              let found = trolleyStatusData.trolley_list.find(
                (statusItem) => statusItem.status_name === statusName
              );
              
              // If no exact match, try normalized comparison (case-insensitive, trimmed)
              if (!found) {
                const normalizedSearchName = statusName.toLowerCase().trim();
                found = trolleyStatusData.trolley_list.find(
                  (statusItem) => statusItem.status_name.toLowerCase().trim() === normalizedSearchName
                );
              }
              
              return found?.id;
            }
          )
          .filter((id): id is number => typeof id === "number");
      }
    }

    const primaryLocationLabel =
      typeof selectedLocationIdValue === "number"
        ? locationMap.get(selectedLocationIdValue)?.label || ""
        : "";

    const payload: FilterValues = {
      primaryLocation: primaryLocationLabel,
      status: normalizedStatus,
      location_id: selectedLocationIdValue,
      from_date: fromDateStr,
      to_date: toDateStr,
    };

    const trimmedPriority = priorityValue.trim();

    if (isTote) {
      payload.priority = trimmedPriority;
      if (trimmedPriority && totePriorityData) {
        const match = totePriorityData.find(
          (option) =>
            option.priority.toLowerCase() === trimmedPriority.toLowerCase()
        );
        payload.priority_ids = match ? [match.id] : undefined;
      } else {
        payload.priority_ids = undefined;
      }
      payload.status_ids = statusIds;
    } else {
      payload.priority = trimmedPriority;
      payload.status_ids = statusIds;
      if (trimmedPriority) {
        payload.priority_flag = trimmedPriority === "High";
      } else {
        payload.priority_flag = undefined;
      }
    }

    return payload;
  };

  const handleReset = () => {
    // Clear all filters (empty state)
    setPriority("");
    setStatus([]);
    setStartDateTime(null);
    setEndDateTime(null);
    setSelectedLocationId(undefined);
    
    // Apply empty filters to refresh the list in background
    const payload = buildFilterPayload(
      "",
      [],
      undefined,
      null,
      null
    );
    onApply(payload);
    
    // Modal stays open for user to enter new filters
  };

  const handleApply = () => {
    if (startDateTime && endDateTime && endDateTime.isBefore(startDateTime)) {
      showInvalidRangeWarning();
      return;
    }

    const payload = buildFilterPayload(
      priority,
      status,
      selectedLocationId,
      startDateTime,
      endDateTime
    );
    onApply(payload);
    onClose();
  };

  // Check if any filter is applied
  const hasFiltersApplied = Boolean(
    priority ||
    status.length > 0 ||
    selectedLocationId ||
    startDateTime ||
    endDateTime
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className="filter-modal"
      placement="bottom"
      height="auto"
      closable={false}
      mask={true}
      styles={{
        body: {
          padding: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        },
        content: {
          paddingBottom: 0,
          background: '#fff',
        },
        wrapper: {
          // Ensure wrapper doesn't start too high
          top: 'auto',
        },
      }}
    >
      {/* Floating close button at the top - positioned relative to drawer body */}
      <div className="filter-close-button" onClick={onClose}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      <div className="filter-content">
        <div className="filter-header">
          <h2>Filter {isTote ? "Tote" : "Trolley"}</h2>
          <div className="filter-header-actions">
            <button className="reset-header-button" onClick={handleReset}>
              Reset
            </button>
          </div>
        </div>

        <div className="filter-scrollable-content">
          <div className="filter-section">
            <label className="filter-label">Primary Location</label>
            <Select<number>
              placeholder="Select location"
              value={selectedLocationId}
              onChange={(value) => {
                if (value === selectedLocationId) {
                  return;
                }
                setSelectedLocationId(value);
              }}
              className="filter-input"
              size="large"
              options={locationOptions.map((option) => ({
                label: option.label,
                value: option.value,
              }))}
              allowClear
            />
          </div>

          <div className="filter-section">
            <label className="filter-label">
              {isTote ? "Start Date & Time" : "Registered Start Date & Time"}
            </label>
            <DatePicker
              showTime={{ format: "HH:mm" }}
              value={startDateTime}
              onChange={(value) => handleStartDateChange(value)}
              className="filter-input"
              size="large"
              format="DD MMM YYYY, HH:mm"
              disabledDate={(current) =>
                endDateTime ? current?.isAfter(endDateTime, "day") : false
              }
              placement="bottom"
              getPopupContainer={() => document.body}
              allowClear
              needConfirm
            />
          </div>

          <div className="filter-section">
            <label className="filter-label">
              {isTote ? "End Date & Time" : "Registered End Date & Time"}
            </label>
            <DatePicker
              showTime={{ format: "HH:mm" }}
              value={endDateTime}
              onChange={(value) => handleEndDateChange(value)}
              className="filter-input"
              size="large"
              format="DD MMM YYYY, HH:mm"
              disabledDate={(current) =>
                startDateTime ? current?.isBefore(startDateTime, "day") : false
              }
              disabledTime={disabledEndTime}
              disabled={!!startDateTime && !startDateTime.isValid()}
              showNow={false}
              needConfirm
              placement="bottom"
              getPopupContainer={() => document.body}
              allowClear
            />
          </div>

          <div className="filter-section">
            <label className="filter-label">Priority</label>
            <div className="chip-group">
              {priorityOptions.map((option) => {
                const isSelected = priority === option;
                return (
                  <button
                    key={option}
                    className={`chip ${isSelected ? "active" : ""}`}
                    onClick={() => {
                      setPriority(isSelected ? "" : option);
                    }}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="filter-section">
            <label className="filter-label">Status</label>
            <div className="chip-group">
              {statusOptions.map((option) => (
                <button
                  key={option}
                  className={`chip ${status.includes(option) ? "active" : ""}`}
                  onClick={() => {
                    if (status.includes(option)) {
                      setStatus(status.filter((s) => s !== option));
                    } else {
                      setStatus([...status, option]);
                    }
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="filter-actions">
          <Button 
            className="apply-button" 
            type="primary" 
            onClick={handleApply}
            disabled={!hasFiltersApplied}
          >
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default FilterModal;
